<template>
    <div
        class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <div class="border-b border-gray-100 px-5 py-3 dark:border-gray-700/50">
            <h3 class="text-sm font-medium text-gray-900 dark:text-white">Histórico</h3>
            <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Toda mudança registra autor, data, alvo, valor anterior, valor novo e motivo. O
                histórico é somente leitura.
            </p>
        </div>

        <form class="flex flex-wrap items-end gap-3 px-5 py-3" @submit.prevent="apply">
            <div class="w-52">
                <single-select v-model="filters.scope" label="Alvo" :options="scopeOptions" />
            </div>
            <div class="w-40">
                <text-input
                    v-model="filters.targetId"
                    label="ID do alvo"
                    placeholder="Opcional"
                />
            </div>
            <button
                type="submit"
                class="rounded-md bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                :disabled="loading"
            >
                Filtrar
            </button>
        </form>

        <div v-if="loading" class="px-5 py-6 text-sm text-gray-500 dark:text-gray-400">
            Buscando registros...
        </div>

        <empty-state
            v-else-if="rows.length === 0"
            title="nenhuma mudança registrada"
            message="O histórico começa na primeira mudança feita no Smaug."
        />

        <div v-else class="overflow-x-auto">
            <table class="w-full table-auto border-collapse text-sm">
                <thead>
                    <tr class="border-b border-black/5 dark:border-white/10">
                        <th
                            v-for="header in [
                                'Data',
                                'Autor',
                                'Alvo',
                                'De',
                                'Para',
                                'Motivo',
                            ]"
                            :key="header"
                            scope="col"
                            class="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400"
                        >
                            {{ header }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in rows"
                        :key="row.id"
                        class="border-b border-black/5 dark:border-white/[0.06]"
                    >
                        <td class="table-cell-sm text-gray-900 dark:text-white">
                            {{ row.created_at }}
                        </td>
                        <td class="table-cell-sm text-gray-900 dark:text-white">
                            {{ row.changed_by_name || "—" }}
                        </td>
                        <td class="table-cell-sm text-gray-900 dark:text-white">
                            {{ targetLabel(row) }}
                        </td>
                        <td class="table-cell-sm text-gray-900 dark:text-white">
                            {{ valueLabel(row.scope, row.old_value) }}
                        </td>
                        <td class="table-cell-sm font-medium text-gray-900 dark:text-white">
                            {{ valueLabel(row.scope, row.new_value) }}
                        </td>
                        <td class="table-cell-sm text-gray-900 dark:text-white">
                            {{ row.reason }}
                        </td>
                    </tr>
                </tbody>
            </table>

            <div v-if="pagination.last_page > 1" class="my-4 flex flex-col items-center">
                <span class="mb-1 text-xs text-gray-500 dark:text-gray-400">
                    Exibindo {{ pagination.from }} a {{ pagination.to }} registros de
                    {{ pagination.total }}
                </span>
                <pagination :pagination="pagination" :offset="1" @paginate="fetch" />
            </div>
        </div>
    </div>
</template>

<script>
import SingleSelect from "../../forms/SingleSelect.vue";
import TextInput from "../../forms/TextInput.vue";
import EmptyState from "../../layouts/EmptyState.vue";
import Pagination from "../../layouts/Pagination.vue";

const SWITCH_LABELS = { off: "Desligado", selective: "Seletivo", all: "Todos" };

const EXPERIENCE_LABELS = { legacy: "Legado", v2: "Checkout 2.0" };

// O "segue" tem nome diferente em cada nivel (PRD -> Telas do Smaug).
const FOLLOW_LABELS = {
    global: "Desligado (padrão)",
    producer: "segue a chave global",
    offer: "segue o produtor",
};

const SCOPE_LABELS = {
    global: "Chave global",
    producer: "Produtor",
    offer: "Oferta",
};

export default {
    name: "ExperienceChangesTable",

    components: { EmptyState, Pagination, SingleSelect, TextInput },

    props: {
        refreshKey: { type: Number, default: 0 },
    },

    data() {
        return {
            loading: true,
            rows: [],
            filters: { scope: null, targetId: "" },
            pagination: {
                current_page: 1,
                last_page: 1,
                from: 0,
                to: 0,
                total: 0,
                per_page: 20,
            },
            scopeOptions: [
                { value: null, label: "Todos os alvos" },
                { value: "global", label: "Chave global" },
                { value: "producer", label: "Produtor" },
                { value: "offer", label: "Oferta" },
            ],
        };
    },

    watch: {
        refreshKey() {
            this.fetch();
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        valueLabel(scope, value) {
            if (value === null) {
                return FOLLOW_LABELS[scope] || "—";
            }

            if (scope === "global") {
                return SWITCH_LABELS[value] || value;
            }

            return EXPERIENCE_LABELS[value] || value;
        },

        targetLabel(row) {
            const scope = SCOPE_LABELS[row.scope] || row.scope;

            return row.scope === "global" ? scope : scope + " #" + row.target_id;
        },

        apply() {
            this.pagination.current_page = 1;
            this.fetch();
        },

        fetch() {
            this.loading = true;

            const params = { page: this.pagination.current_page };

            if (this.filters.scope) {
                params.scope = this.filters.scope;
            }

            if (this.filters.targetId.trim() !== "") {
                params.target_id = this.filters.targetId.trim();
            }

            axios
                .get("/api/checkout-experience/changes", { params })
                .then(({ data }) => {
                    this.rows = data.data;
                    this.pagination = {
                        current_page: data.current_page,
                        last_page: data.last_page,
                        from: data.from,
                        to: data.to,
                        total: data.total,
                        per_page: data.per_page,
                    };
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text: response?.data?.message || "Falha ao carregar o histórico.",
                        },
                        4000,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>
